import {
  getProductSaleKey,
  getProductsSaleKey,
  ProductSaleLiteModel,
  ProductSalesModel,
  useGetProductSale,
  useGetProductsSale,
} from 'api-hooks/product/sales';
import { Option } from 'baseui/select';
import { SaleStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: ProductSaleLiteModel | ProductSalesModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.invoiceNumber, item.customer.name]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: any) => void;
  onAfterDetailChange?: (value: ProductSalesModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function ProductSalesSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:order'), placeholder = t('common:select_order') } =
    props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetProductsSale,
    useDetailLazyQueryHook: useGetProductSale,
    listKeyFunction: getProductsSaleKey,
    detailKeyFunction: getProductSaleKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: { status: SaleStatusEnum.Active, is_completed: false },
        },
      }),
      [],
    ),
    renderCreate: false,
    onClickCreate: React.useCallback(() => {
      props.onClose?.();
    }, [props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, ProductSalesModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      overrides={overrides}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
